import {Button, Tab, Tabs, TextField} from "@mui/material";
import React, {useEffect, useRef, useState} from "react";
import {grey} from "@mui/material/colors";
import {ucFirst} from "../../manage/summary/SummarySection";
import {first, useAsync2, useAsyncAction} from "nate-react-api-helpers";
import {useIsQuote} from "../../quote/QuoteContainer";
import {api} from "../../../../api/API";
import {useProjectId} from "../ProjectName";
import {useAlternative} from "../../quote/alternative/Alternative";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import {PriceSectionNoteDefault} from "./PriceSectionNoteDefault";
import {Div10TypeToName} from "../div10/Div10Tabs";
import {getAll} from "../../../logistics/routes/useGetAll";
import {useSessionStorage} from "react-use";
import {DragGrip} from "../../../../misc/DragGrip";
import {css} from "@emotion/css";
import {tabGroup} from "../../../logistics/purchasing/PartsToSource";
import {TabWrapper} from "../TabWrapper";
import {parseHardwareTabValue, useHardwareCategoryList} from "../../quote/pricing/HardwareTabs";

export function PriceSectionNotes(props: {
    tab: string;
    subTab: string;
}) {
    const [hwTab, setHwTab] = useState("default");
    const hardwareCategories = useHardwareCategoryList();

    if(props.tab === "hardware") {
        const categoryId = parseHardwareTabValue(props.subTab);

        // production mode has no supplier-category subtabs (empty subTab): keep the
        // legacy Finishing/Aluminum toggle so both note variants stay editable.
        if(categoryId === null) {
            const aluminum = hwTab !== "default";

            return (
                <div>
                    <PriceSectionNotesInner
                        {...props}
                        tabs={
                            <TabWrapper>
                                <Tabs className={tabGroup} value={hwTab} onChange={(e, value) => setHwTab(value)}>
                                    <Tab label="Finishing" value="default" />
                                    <Tab label="Aluminum" value="aluminum" />
                                    <div style={{flex: 1}} />
                                </Tabs>
                            </TabWrapper>
                        }
                        keyNameOverride={keyForHardwareCategory(null, "hardware-provider", aluminum)}
                        tabNameOverride={aluminum ? "Aluminum Hardware" : "Finishing Hardware"} />
                </div>
            )
        }

        // quote mode: one notes box per supplier-category subtab (no toggle). The
        // Standard/Aluminum hardware children reuse the legacy "hardware" /
        // "hardware/AL" keys so existing notes carry over.
        const category = hardwareCategories.find(c => c.id === categoryId);

        return <PriceSectionNotesInner {...props}
            keyNameOverride={noteKeyForHardwareCategory(categoryId, category?.internalLabel ?? null)}
            tabNameOverride={category?.name || "Hardware"} />
    }

    return <PriceSectionNotesInner {...props} />
}

const alSubtab = "AL"
export const hardwareAlKeyName = "hardware/AL"

// keyForHardwareCategory maps a supplier-category subtab + Finishing/Aluminum
// variant to the note storage key. The hardware-provider root (and production's
// empty subtab) keep the legacy "hardware" / "hardware/AL" keys so existing notes
// are preserved; other categories use "hardware/cat-<id>" (+ "/AL").
export function keyForHardwareCategory(categoryId: number | null, internalLabel: string | null, aluminum: boolean): string {
    if(internalLabel === "hardware-provider" || categoryId === null) {
        return aluminum ? hardwareAlKeyName : "hardware";
    }

    return aluminum ? `hardware/cat-${categoryId}/AL` : `hardware/cat-${categoryId}`;
}

// noteKeyForHardwareCategory maps a supplier-category subtab to its single note
// storage key. Standard/Aluminum hardware reuse the legacy "hardware" /
// "hardware/AL" keys so existing notes carry over; other supplier categories use
// "hardware/cat-<id>".
function noteKeyForHardwareCategory(categoryId: number | null, internalLabel: string | null): string {
    if(internalLabel === "aluminum-hardware-provider") return hardwareAlKeyName;
    if(internalLabel === "standard-hardware-provider" || internalLabel === "hardware-provider" || categoryId === null) return "hardware";
    return `hardware/cat-${categoryId}`;
}

function PriceSectionNotesInner(props: {
    tab: string;
    subTab: string;
    tabs?: any;
    keyNameOverride?: string;
    tabNameOverride?: string;
}) {
    const [value, setValue] = useState("")
    const isQuote = useIsQuote();
    const project = useProjectId();
    const alternative = useAlternative();

    const tab = (() => {
        if(props.keyNameOverride !== undefined) return props.keyNameOverride;
        if(props.tab === "door" && props.subTab === "wd") return "door/wd";
        if(props.tab === "hardware" && props.subTab === alSubtab) return hardwareAlKeyName;
        if(props.tab === "div10") return `${props.tab}/${props.subTab}`;
        return props.tab;
    })();

    const tabName = (() => {
        if(props.tabNameOverride !== undefined) return props.tabNameOverride;
        if(props.tab === "door" && props.subTab === "wd") return "Wood Doors";

        switch(props.tab) {
            case "door-hm":
                return "HM Door";
            case "door-wd":
                return "WD Door";
            case "frame-anchor":
                return "Anchors"
            case "frame-hm":
                return "HM Frame"
            case "div10":
                if(props.subTab) {
                    return Div10TypeToName(props.subTab)
                }

                break;
            case "hardware":
                if(props.subTab === "AL") {
                    return "Aluminum Hardware"
                }

                return "Finishing Hardware"
        }

        return sectionName(props.tab)
    })();

    const get = useAsync2(async (input) => {
        const list = await getAll(null, offset => isQuote ? api.quotePriceSectionNotes.list({offset, ...input}) : api.priceSectionNotes.list({offset, ...input}))
        return first(list, l => l.keyName === input.tab);
    }, {
        project: project,
        alternative: alternative,
        tab: tab,
    }, [project, alternative, tab]);

    const fromServer = get.result;

    useEffect(() => {
        setValue(fromServer?.note || "");
    }, [fromServer])

    const timer = useRef<any>(null);
    const update = useAsyncAction(async (input) => {
        if(isQuote) {
            return api.quotePriceSectionNotes.upsert(input)
        }

        delete input.quoteAlternative;
        return api.priceSectionNotes.upsert(input)
    }, [isQuote])

    const [defaultAnchor, setDefaultAnchor] = useState<HTMLElement | null>(null);
    const textUpdated = (value: string, keyName: string) => {
        setValue(value);

        if(timer.current) {
            clearTimeout(timer.current);
        }

        timer.current = setTimeout(() => {
            timer.current = null;

            update.callback({
                project: project,
                quoteAlternative: alternative,
                keyName: keyName,
                note: value,
            });
        }, 1000);
    }

    const wrapperRef = useRef<HTMLDivElement|null>(null);
    const [height, setHeight] = useSessionStorage(`price-section-notes-height-tab=${props.tab}-${props.subTab},project=${project},alt=${alternative}`, 150);

    if(tab === "misc") return null;

    return (
        <div style={{flex: 0, padding: 8}}>
            <div style={{display: "flex", alignItems: "center"}}>
                <div style={{fontSize: "0.8rem", color: grey["600"], paddingLeft: 4, paddingBottom: 4}}>
                    Customer Price Notes for {tabName}
                </div>
                <div style={{flex: 1}}>
                </div>
                <DragGrip<HTMLDivElement>
                    modifyRef={wrapperRef}
                    getValue={g => parseInt(g.style.height, 10)}
                    mode="vertical-inverse"
                    setValue={(g, value) => {
                        g.style.height = value + "px";
                    }}
                    onComplete={value => {
                        setHeight(value);
                    }}
                />
            </div>
            {props.tabs}
            <div style={{position: "relative", height: height}} ref={wrapperRef}>
                {get.LoadingOrErrorElement || <TextField className={autoGrowTextFieldCss} multiline onChange={e => {
                    textUpdated(e.target.value, tab);
                }} value={value} fullWidth />}
                <div style={{position: 'absolute', zIndex: 100, right: 8, top: 8}}>
                    <Button onClick={e => setDefaultAnchor(e.currentTarget)} endIcon={<ArrowDropDown />}>Add Default</Button>
                    {defaultAnchor && <PriceSectionNoteDefault keyName={tab} anchor={defaultAnchor}
                        onClose={(toAdd: string[]) => {
                            if(toAdd.length > 0) {
                                textUpdated((value + "\n" + toAdd.join("\n")).trim(), tab)
                            }

                            setDefaultAnchor(null);
                        }} />}
                </div>
            </div>

            {update.error ? update.LoadingElement : null}
        </div>
    )
}

export function sectionName(name: string) {
    switch (name) {
        case "summary": return "General";
        case "door": return "Doors";
        case "frame": return "Frames";
        case "hardware": return "Finishing Hardware";
        default:
            return ucFirst(name);
    }
}

const autoGrowTextFieldCss = css({
    height: "100%",
    "& .MuiOutlinedInput-root": {
        flex: 1,
    },

    "& .MuiInputBase-input": {
        alignSelf: "flex-start",
    }
})